import React, { useEffect, useRef } from "react";
import {
  Tab,
  Tabs,
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import UpcomingMeetings from "./UpcomingMeetings";
import PreviousMeetings from "./PreviousMeetings";
import KeyDiscussions from "./KeyDiscussions";
import NextBestOffers from "./NextBestOffers";
import { useGlobalModalContext } from "../context/GlobalModalContext";
import { useLoading } from "../context/LoadingContext";
import { useState } from "react";
import { fileUploadaxiosInstance } from "../utils/axios";
import MeetingMinuteDialog from "./Modal/MeetingMinuteDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import MeetingSchedulerDialog from "./Modal/MeetingSchedulerDialog";
import CustomTooltip from "./CustomTooltip";
import { useChatbotContext } from "../context/ChatbotContext";
import { useAuth } from "../context/AuthContextProvider";

const ClientMeetingPrep = () => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const { showModal, hideAllModal, hideModal } = useGlobalModalContext();
  const [selectedLanguage, setSelectedLanguage] = useState("Bahasa");
  const [file, setFile] = useState("");
  const [fileError, setFileError] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const hasRun = useRef(false);
  const { setSessionId } = useChatbotContext();

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleSubmit = async (e) => {
    showLoading();
    try {
      e.preventDefault();
      setFileError(false);
      if (!file) {
        setFileError(true);
        return;
      }
      const response = await fileUploadaxiosInstance.post("/MOM/generate", {
        language: selectedLanguage,
        file: file,
      });

      if (response.data) {
        hideModal();
        showModal(<MeetingMinuteDialog data={response.data} />);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      hideLoading();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setFile(file);
      setFileError(false);
    }
  };

  const handleScheduler = (data) => {
    showModal(<MeetingSchedulerDialog emails={data?.participant_emails} />);
  };

  const tabsData = [
    {
      label: "Upcoming Meeting",
      tooltip: "See planned meetings, topics for discussion, and attendee details"
    },
    {
      label: "Previous Meeting", 
      tooltip: "Access notes, action items, and attendee details from past meetings"
    },
    {
      label: "Key Discussion Topics",
      tooltip: "Suggested discussion points to enhance client conversations"
    },
    {
      label: "Next Best Offers",
      tooltip: "View personalized offers tailored for the client"
    }
  ];

  return (
    <Box className="bg-gray-100">
      {/* <div className="relative"> */}
      <form onSubmit={handleSubmit}>
        <div
          className="flex flex-row gap-2 my-4 p-4"
          style={{ border: "1px dotted #808080", borderRadius: "8px" }}
        >
          {/* Meeting Notes Column */}
          <div className="flex-[4]">
            <div>
              <div className="text-xs font-bold my-2">Meeting Notes:</div>
              <div className="text-xs" style={{ color: "grey" }}>
                {/* Instantly generate minutes of meeting, send email or schedule
                follow up meeting */}
                Upload a text file or voice notes in MP3/WAV format in your preferred language, Bahasa or English, to instantly generate meeting minutes. Also send an email or schedule a follow-up meeting with your client to discuss key topics
              </div>
            </div>
          </div>

          {/* Language Selection Column */}
          <div className="flex-[2] ml-4 mt-4">
            <FormControl size="small" required error={fileError}>
              <input
                type="file"
                accept=".txt, .wav, .mp3"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="file-input"
              />
              {/* <FormLabel htmlFor="file-input"></FormLabel> */}

              <div className="flex flex-col">
                <label htmlFor="file-input" style={{ height: "30px" }}>
                  <Tooltip title="Upload text file in txt format or voice note in MP3 or WAV format to generate minutes">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        marginTop: "10px",
                        width: "200px",
                        ...(fileError && {
                          color: "red",
                          borderColor: "red"
                        }),
                      }}
                      size="small"
                    >
                      Upload File
                    </Button>
                  </Tooltip>
                </label>
                <CustomTooltip title="Functionality not yet integrated.">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<KeyboardVoiceIcon />}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginTop: "14px",
                      width: "200px",
                      height: "30px",
                    }}
                    size="small"
                  >
                    Start Recording
                  </Button>
                </CustomTooltip>
                {file && (
                  <span className="text-xs">
                    <i>Selected File: {file.name}</i>
                  </span>
                )}
              </div>
            </FormControl>
          </div>

          {/* Next Column */}
          <div className="flex-[2] ml-6 mt-4">
            <div className="flex items-end gap-5">
              <div className="text-xs" style={{ color: "grey" }}>MoM Language: </div>
            <FormControl
              fullWidth
              size="small"
              required
              variant="standard"
              sx={{
                width: 130,
                height: "30px",
                "& .MuiInput-root": {
                  "&:before": {
                    borderBottom: "1px solid #00555C",
                  },
                  "&:hover:not(.Mui-disabled):before": {
                    borderBottom: "2px solid #00555C",
                  },
                  "&.Mui-focused:after": {
                    borderBottom: "2px solid #00555C",
                  },
                },
              }}
            >
              {/* <FormLabel
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginTop: "10px",
                }}
              >
                Language
              </FormLabel> */}
              <Select
                size="small"
                value={selectedLanguage}
                onChange={(e) => {
                  setSelectedLanguage(e.target.value);
                }}
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#00555C",
                  "& .MuiSelect-select": {
                    paddingTop: 1,
                  },
                  "& .MuiSelect-icon": {
                    color: "#00555C",
                  },
                }}
                IconComponent={ExpandMoreIcon}
              >
                <MenuItem value={"Bahasa"}>Bahasa</MenuItem>
                <MenuItem value={"English"}>English</MenuItem>
              </Select>
            </FormControl>
            </div>
            <Button
              type="submit"
              variant="contained"
              size="small"
              startIcon={<TextSnippetIcon />}
              sx={{ textTransform: "none", mt: 2, width: "240px" }}
            >
              Generate MOM in {selectedLanguage}
            </Button>
          </div>

          {/* Calendar Icon Column */}
          <div className="flex-[1] flex justify-end items-top">
            <CustomTooltip title="Functionality not yet integrated.">
              <CalendarMonthIcon color="primary" onClick={() => handleScheduler()} />
            </CustomTooltip>
          </div>
        </div>
      </form>
      {/* </div> */}

      {/* Tabs for navigation */}
      <Box sx={{ border: "1px dotted #808080", borderRadius: "8px" }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          className="my-4"
          sx={{
            minHeight: "32px",
            borderBottom: 1,
            borderColor: "divider",
            width: "100%",
          }}
        >
          {tabsData.map((tab, index) => (
            <Tooltip key={index} title={tab.tooltip}>
              <Tab
                label={tab.label}
                sx={{
                  color: "black",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  padding: "0.25rem 1rem",
                  textTransform: "none",
                  minWidth: "auto",
                  minHeight: "32px",
                  "&.Mui-selected": {
                    backgroundColor: "#00555C",
                    color: "white",
                  },
                  "&:hover": {
                    backgroundColor: tabIndex === index ? "#00555C" : "#f0f0f0",
                    color: tabIndex === index ? "white" : "black",
                  },
                }}
              />
            </Tooltip>
          ))}
        </Tabs>

        {/* Content for the selected tab */}
        {tabIndex === 0 && <UpcomingMeetings />}
        {tabIndex === 1 && <PreviousMeetings />}
        {tabIndex === 2 && <KeyDiscussions />}
        {tabIndex === 3 && <NextBestOffers />}
      </Box>
    </Box>
  );
};

export default ClientMeetingPrep;
